import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import isEqual from 'lodash/isEqual';
import debounce from 'lodash/debounce';

import { getCompany, getSubcompany, getPermissions } from 'auth/selectors';
import {
  getPagination,
  getIsLoading,
  getVisibleAssets,
  getFilters,
  getNeedFetch,
} from 'selectors/reports_depreciation';
import {
  getVisibleAccountsParent,
  getVisibleCostCenters,
  getVisibleEmployees,
  getVisibleStatus,
} from 'selectors/lists';

import AssetsFormConfig from 'containers/assets/AssetsFormConfig';
import {
  resetReport,
  getReport,
  setPagination,
  setFilters,
  getDepreciationCSV,
  getDepreciationXLSX,
  getDepreciationPDF,
} from 'containers/reports/actions_depreciation';

import Reports from 'components/reports';

import { valueFromEvent, saveAsCSV } from 'lib/util';
import { generateDepreciationPDF } from 'lib/pdfs/DepreciationPDF';
import FileSaver from 'file-saver';
import moment from 'moment';

class DepreciationReportContainer extends PureComponent {
  constructor() {
    super();
    this.fetchDebounced = debounce(this.fetch, 200);
  }

  componentWillMount = () => {
    this.props.reset();
    this.fetch(this.props.pagination, this.props.filters);
  };

  fetch = (pagination, filters) => {
    const { company, subcompany } = this.props;

    this.props.getReport(
      company.id,
      subcompany.id,
      filters,
      pagination.limit,
      pagination.page,
      pagination.orderBy,
      pagination.order
    );
  };

  componentWillReceiveProps = (nextProps) => {
    if (
      nextProps.needFetch &&
      (!isEqual(nextProps.pagination, this.props.pagination) ||
        !isEqual(nextProps.filters.accounts, this.props.filters.accounts) ||
        !isEqual(nextProps.filters.status, this.props.filters.status) ||
        !isEqual(nextProps.filters.costCenters, this.props.filters.costCenters) ||
        !isEqual(nextProps.filters.employees, this.props.filters.employees) ||
        !isEqual(nextProps.filters.fulltext, this.props.filters.fulltext) ||
        !isEqual(nextProps.filters.endDate, this.props.filters.endDate))
    ) {
      this.fetchDebounced(nextProps.pagination, nextProps.filters);
    }
  };

  componentWillUnmount = () => this.props.reset();

  handleOrderChange = (orderBy, order) => this.props.setPagination({ orderBy, order });

  handleLimitChange = (limit) => this.props.setPagination({ limit: parseInt(limit, 10) });

  handlePageChange = (page) => this.props.setPagination({ page: parseInt(page, 10) });

  handleFiltersChange = (e) => this.props.setFilters(valueFromEvent(e));

  handleFiltersSubmit = (e) => {
    e.preventDefault();
    this.fetch(this.props.pagination, this.props.filters);
  };

  handleExport = (e) => {
    const { company, subcompany } = this.props;

    this.props
      .getDepreciationCSV(company.id, subcompany.id, this.props.filters)
      .then(({ value: { data } }) => saveAsCSV(`depreciacion`, this.props.filters.endDate, data))
      .catch((e) => {});
  };

  /**
   * @author Jorge
   * @creationDate 27/04/2024
   *
   * @description Export depreciation assets list to XLSX format file
   *
   * @param {object} e - Event object
   * @returns {void}
   */
  handleExportXLSX = (e) => {
    const { company, subcompany } = this.props;

    this.props
      .getDepreciationXLSX(company.id, subcompany.id, this.props.filters)
      .then((response) => {
        if (
          response &&
          response.value &&
          response.value.data &&
          response.value.data.type === 'Buffer' &&
          Array.isArray(response.value.data.data) &&
          response.value.data.data.length > 0
        ) {
          const dataArray = new Uint8Array(response.value.data.data);
          const blob = new Blob([dataArray], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });
          
          FileSaver.saveAs(blob, `depreciacion-${moment().format('DDMMYYYY')}.xlsx`);
        } else {
          console.error('La respuesta no contiene datos válidos:', response);
        }
      })
      .catch((error) => {
        console.error('Error al exportar XLSX:', error);
      });
  };

  handleExportPDF = (e) => {
    const { company, subcompany } = this.props;

    this.props
      .getDepreciationPDF(company.id, subcompany.id, this.props.filters)
      .then(({ value: { data } }) =>
        generateDepreciationPDF(
          this.props.subcompany,
          this.props.filters.endDate,
          this.props.filters.depreciationType,
          data
        )
      )
      .catch((e) => {
        console.log(e);
      });
  };

  renderRows = () =>
    this.props.assets.map((asset) => (
      <Reports.DepreciationRow
        key={asset.id}
        asset={asset}
        depreciation={this.props.filters.depreciationType}
      />
    ));

  render = () => {
    return (
      <AssetsFormConfig
        render={() => (
          <Reports.DepreciationList
            accounts={this.props.accounts}
            costCenters={this.props.costCenters}
            employees={this.props.employees}
            status={this.props.status}
            companyId={this.props.company.id}
            filters={this.props.filters}
            isLoading={this.props.isLoading}
            pagination={this.props.pagination}
            permissions={this.props.permissions}
            subcompanyId={this.props.subcompany.id}
            count={this.props.assets.length}
            handleExport={this.handleExport}
            handleExportXLSX={this.handleExportXLSX}
            handleExportPDF={this.handleExportPDF}
            handleFiltersSubmit={this.handleFiltersSubmit}
            handleFiltersChange={this.handleFiltersChange}
            handleOrderChange={this.handleOrderChange}
            handleLimitChange={this.handleLimitChange}
            handlePageChange={this.handlePageChange}
          >
            {this.renderRows()}
          </Reports.DepreciationList>
        )}
      />
    );
  };
}

const mapStateToProps = (state) => ({
  company: getCompany(state),
  subcompany: getSubcompany(state),
  isLoading: getIsLoading(state),
  pagination: getPagination(state),
  permissions: getPermissions(state),
  assets: getVisibleAssets(state),
  filters: getFilters(state),
  needFetch: getNeedFetch(state),
  accounts: getVisibleAccountsParent(state),
  costCenters: getVisibleCostCenters(state),
  employees: getVisibleEmployees(state),
  status: getVisibleStatus(state),
});

const mapDispatchToProps = {
  getReport,
  setPagination,
  setFilters,
  getDepreciationCSV,
  getDepreciationXLSX,
  reset: resetReport,
  getDepreciationPDF,
};

export default connect(mapStateToProps, mapDispatchToProps)(DepreciationReportContainer);



// WEBPACK FOOTER //
// src/containers/reports/ReportsDepreciationContainer.js